// tslint:disable: cyclomatic-complexity
import * as React from 'react';
import { ThemeContext } from 'styled-components';

import { LoadingSpinner } from '../Spinner/LoadingSpinner';
import { getNumValFromStr, VALID_DIMENSION_DATA, VALID_EDGE_STYLE_DATA, VALID_MEDIA_STYLES } from '../Utils';
import { VALID_GLOBAL_FONT_SIZES } from '../Typography/common';
import { VALID_ICON_ROTATION, VALID_ICONS } from '../../Icon/SVGIcon';

import { IButtonCommonProps } from './common';
import { ThemedButton } from './StyledButton';
import LoadingWrapper from './ButtonLoadingWrapper';
import ButtonChildWrapper from './ButtonChildWrapper';

export interface IIconProps {
  icon: VALID_ICONS;
  width?: string;
  height?: string;
  color: string;
  margin?: VALID_EDGE_STYLE_DATA;
  rotate?: VALID_ICON_ROTATION;
}

export interface IButtonIcon {
  before?: IIconProps;
  after?: IIconProps;
}

export interface IButtonProps extends IButtonCommonProps, React.ButtonHTMLAttributes<HTMLButtonElement> {
  isLoading?: boolean;
  margin?: VALID_EDGE_STYLE_DATA;
  order?: VALID_MEDIA_STYLES;
  width?: VALID_DIMENSION_DATA;
  iconProps?: IButtonIcon;
  error?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, IButtonProps>((props, ref) => {
  const {
    alignSelf,
    flexBasis,
    flexGrow,
    flexShrink,
    children,
    error,
    iconProps,
    isLoading,
    margin,
    order,
    variant,
    width,
    ...rest
  } = props;
  const { themeTwo: { name, global: { edgeSize, font }, button }} = React.useContext(ThemeContext);
  // false sets default line-height found here StyledButton.ts:34
  const lineHeight = button.base.font.lineHeight ? button.base.font.lineHeight : '1.2';
  // false sets default font-size found here StyledButton.ts:32
  const fontSize = button.base.font.size ? font.size[button.base.font.size as VALID_GLOBAL_FONT_SIZES] : '1.6rem';
  const insideSpinnerSize = `${getNumValFromStr(lineHeight) * (getNumValFromStr(fontSize) * 10)}px`;
  const outsideSpinnerSize = !button.base.loadingIconInsideButton && edgeSize[button.base.spinner.size];
  const isTextOrLink = variant === 'link' || variant === 'text';
  const hasLoadingState = !!(isLoading && !isTextOrLink);
  const availableVariants = Object.keys(button.overrides);
  // this defaults button variants to avoid failures within consuming applications
  let variantType = 'primary';

  if (!availableVariants.includes(variant)) {
    console.error(`
      ${variant} is not available. The following variants are available for ${name}'s theme: ${Object.values(availableVariants)}
    `);
  } else {
    variantType = variant;
  }

  /* tslint:disable: no-non-null-assertion */
  let themeIconProps =
    button.overrides[variantType].base &&
    button.overrides[variantType].base!.icons;

  if (rest.disabled && button.overrides[variantType].base && button.overrides[variantType].base!.icons) {
    themeIconProps =
      button.overrides[variantType] &&
      button.overrides[variantType].state.disabled &&
      button.overrides[variantType].state.disabled.icons;
  }
  if (error && button.overrides[variantType].base && button.overrides[variantType].base!.icons) {
    themeIconProps =
      button.overrides[variantType] &&
      button.overrides[variantType].state.error &&
      button.overrides[variantType].state.error!.icons;
  }
  /* tslint:enable: no-non-null-assertion */

  return (
    <LoadingWrapper
      isLoading={isLoading}
      variant={variantType}
      margin={hasLoadingState && !button.base.loadingIconInsideButton ? margin : ''}
      width={width}
    >
      <ThemedButton
        {...rest}
        alignSelf={alignSelf}
        flexBasis={flexBasis}
        flexGrow={flexGrow}
        flexShrink={flexShrink}
        // margin needs to move to the wrapper when in loading state and loading icon is beside the button
        marginProp={(isLoading && button.base.loadingIconInsideButton) || !isLoading ? margin : ''}
        orderProp={order}
        variant={variantType}
        isLoading={isLoading}
        role={isLoading ? 'alert' : rest.role}
        aria-live={isLoading ? 'polite' : rest['aria-live']}
        aria-label={isLoading ? 'Loading do not refresh the page' : rest['aria-label']}
        disabled={isLoading ? true : rest.disabled}
        widthProp={width}
        ref={ref}
        error={error}
      >
        {hasLoadingState && button.base.loadingIconInsideButton ?
          <span>
            <LoadingSpinner size={insideSpinnerSize} />
          </span>
          :
          /* wrapper controls where/if icons are rendered */
          <ButtonChildWrapper
            iconProps={themeIconProps ? themeIconProps : iconProps}
            disabled={rest.disabled}
            variant={variantType}
          >
            {children}
          </ButtonChildWrapper>
        }
      </ThemedButton>
      {hasLoadingState && !button.base.loadingIconInsideButton && <LoadingSpinner size={outsideSpinnerSize} />}
    </LoadingWrapper>
  );
});

export default Button;
